"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { getOutlets, updateUserProfile } from "@/firebase/firestoreServices";
import type { Outlet } from "@/types";
import { useAuth } from "./AuthContext";
import { useGeolocation, haversineDistanceKm } from "@/hooks/useGeolocation";
import { toast } from "sonner";

interface OutletContextType {
  outlets: Outlet[];
  selectedOutlet: Outlet | null;
  setSelectedOutlet: (outlet: Outlet) => void;
  loading: boolean;
  isOutOfRange: boolean;
  userCoords: { lat: number | null; lng: number | null };
  displayAddress: string;
  selectedAddressId: string | null;
  setSelectedAddressById: (id: string) => void;
  requestLocation: () => void;
  geoStatus: "idle" | "requesting" | "granted" | "denied" | "unavailable";
  geoError: string | null;
  outletCity: string | null;
}

const OutletContext = createContext<OutletContextType>({
  outlets: [],
  selectedOutlet: null,
  setSelectedOutlet: () => { },
  loading: true,
  isOutOfRange: false,
  userCoords: { lat: null, lng: null },
  displayAddress: "Select Location",
  selectedAddressId: null,
  setSelectedAddressById: () => { },
  requestLocation: () => { },
  geoStatus: "idle",
  geoError: null,
  outletCity: null,
});

export const useOutlet = () => useContext(OutletContext);

export const OutletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useAuth();
  const { lat, lng, error: geoError, requestLocation, status: geoStatus } = useGeolocation();
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOutOfRange, setIsOutOfRange] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [displayAddress, setDisplayAddress] = useState("Select Location");
  const [outletCity, setOutletCity] = useState<string | null>(null);

  // 0. Toasts for geolocation feedback
  useEffect(() => {
    if (geoStatus === "requesting") {
      toast.info("Detecting your location...");
    } else if (geoStatus === "denied") {
      toast.error(geoError || "Location access denied");
    } else if (geoStatus === "unavailable") {
      toast.error("Geolocation is not supported by your browser");
    }
  }, [geoStatus, geoError]);

  // 1. Fetch all outlets
  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const data = await getOutlets();
        const outletList = data as Outlet[];
        setOutlets(outletList);
      } catch (e) {
        console.error("Error fetching outlets:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchOutlets();
  }, []);

  // 1.5 Sync selectedAddressId with profile default if not manually set
  useEffect(() => {
    if (profile?.defaultAddressId && !selectedAddressId) {
      setSelectedAddressId(profile.defaultAddressId);
    }
  }, [profile?.defaultAddressId, selectedAddressId]);

  // 2. Logic to find nearest outlet and check range
  useEffect(() => {
    if (outlets.length === 0) return;

    // Helper to extract city from address string
    const extractCity = (address: string) => {
      const parts = address.split(",");
      if (parts.length >= 2) {
        const gujaratIdx = parts.findIndex(p => p.toLowerCase().includes("gujarat"));
        if (gujaratIdx > 0) return parts[gujaratIdx - 1].trim();
        return parts[parts.length - 2].trim();
      }
      return "";
    };

    const runRangeCheck = (targetLat: number, targetLng: number) => {
      let nearest: Outlet | null = null;
      let minDistance = Infinity;
      let nearestInRange = false;

      outlets.forEach((o) => {
        if (o.lat && o.long) {
          const dist = haversineDistanceKm(targetLat, targetLng, o.lat, o.long);
          const radius = o.radius || 5;
          const isInRange = dist <= radius;

          if (!nearest || (isInRange && !nearestInRange) || (isInRange && nearestInRange && dist < minDistance) || (!isInRange && !nearestInRange && dist < minDistance)) {
            minDistance = dist;
            nearest = o;
            nearestInRange = isInRange;
          }
        }
      });

      if (nearest) {
        const outlet = nearest as Outlet;
        setIsOutOfRange(!nearestInRange);
        setOutletCity(extractCity(outlet.outletAddresss || outlet.outletAddress || ""));
        if (!selectedOutlet || selectedOutlet.id !== outlet.id) {
          setSelectedOutlet(outlet);
        }
      }
    };

    // Determine target coordinates (Selected address takes precedence over browser GPS)
    let targetLat = lat;
    let targetLng = lng;

    if (selectedAddressId && profile?.addresses?.[selectedAddressId]) {
      const addr = profile.addresses[selectedAddressId];
      if (addr.lat && addr.long) {
        // Address has stored coordinates — use them directly
        targetLat = addr.lat;
        targetLng = addr.long;
        runRangeCheck(targetLat, targetLng);
        return;
      } else if (addr.area && addr.city) {
        // Address has no coordinates — geocode on the fly
        const q = encodeURIComponent(`${addr.area}, ${addr.city}, ${addr.state || "Gujarat"}, India`);
        fetch(`https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=1`, {
          headers: { "Accept-Language": "en" }
        })
          .then(res => res.json())
          .then(data => {
            if (data && data.length > 0) {
              const geoLat = parseFloat(data[0].lat);
              const geoLng = parseFloat(data[0].lon);
              runRangeCheck(geoLat, geoLng);
              // Save coordinates back to Firestore so future checks use stored coords
              if (user) {
                updateUserProfile(user.uid, {
                  [`addresses.${selectedAddressId}.lat`]: geoLat,
                  [`addresses.${selectedAddressId}.long`]: geoLng,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any);
              }
            } else if (targetLat !== null && targetLng !== null) {
              // Geocoding failed — fall back to GPS if available
              runRangeCheck(targetLat, targetLng);
            }
          })
          .catch(() => {
            // Geocoding error — fall back to GPS if available
            if (targetLat !== null && targetLng !== null) {
              runRangeCheck(targetLat, targetLng);
            }
          });
        return; // async geocoding will call runRangeCheck
      }
    }

    // No selected address with coords — use GPS if available
    if (targetLat === null || targetLng === null) return;
    runRangeCheck(targetLat, targetLng);
  }, [outlets, lat, lng, selectedOutlet, selectedAddressId, profile?.addresses, user]);

  // 3. Fallback sync for selected outlet from profile or local storage (if geo fails)
  useEffect(() => {
    if (outlets.length === 0 || selectedOutlet) return;

    if (user && profile?.selectedOutletDocId) {
      const found = outlets.find((o) => o.id === profile.selectedOutletDocId);
      if (found) {
        setSelectedOutlet(found);
        return;
      }
    }

    const saved = typeof window !== "undefined" ? localStorage.getItem("selectedOutlet") : null;
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const found = outlets.find((o) => o.id === parsed.id);
        if (found) {
          setSelectedOutlet(found);
          return;
        }
      } catch (e) {
        console.error("Error parsing saved outlet:", e);
      }
    }

    // Always fallback to first outlet so products always load
    // (geo or profile data can refine this later)
    if (!selectedOutlet && outlets.length > 0) {
      setSelectedOutlet(outlets[0]);
      setDisplayAddress(outlets[0].outletName || "Fresh O Meat");
    }
  }, [user, profile?.selectedOutletDocId, outlets, selectedOutlet, geoStatus]);

  // 4. Update display address based on source
  useEffect(() => {
    if (selectedAddressId && profile?.addresses?.[selectedAddressId]) {
      const addr = profile.addresses[selectedAddressId];
      setDisplayAddress(addr.area || addr.name || "My Address");
    } else if (selectedOutlet) {
      // Always show outlet name, whether from GPS or fallback
      setDisplayAddress(selectedOutlet.outletName || "Fresh O Meat");
    }
  }, [selectedAddressId, profile?.addresses, geoStatus, selectedOutlet]);

  const handleSetOutlet = async (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    localStorage.setItem("selectedOutlet", JSON.stringify(outlet));
    if (user) {
      try {
        await updateUserProfile(user.uid, { selectedOutletDocId: outlet.id });
      } catch (err) {
        console.error("Error updating profile outlet:", err);
      }
    }
  };

  return (
    <OutletContext.Provider value={{
      outlets,
      selectedOutlet,
      setSelectedOutlet: handleSetOutlet,
      loading,
      isOutOfRange,
      userCoords: { lat, lng },
      displayAddress,
      selectedAddressId,
      setSelectedAddressById: setSelectedAddressId,
      requestLocation,
      geoStatus,
      geoError,
      outletCity,
    }}>
      {children}
    </OutletContext.Provider>
  );
};

